import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Hook that calls a callback whenever the window is resized.
 *
 * Uses a ref-based pattern to always call the latest callback
 * without needing it as a useEffect dependency. This means:
 * - The resize listener is added once on mount
 * - The callback can reference any state/props without causing re-subscriptions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [size, setSize] = useState({ width: 0, height: 0 });
 *
 *   useWindowResize(() => {
 *     setSize({ width: window.innerWidth, height: window.innerHeight });
 *   });
 *
 *   return <div>Window: {size.width} x {size.height}</div>;
 * }
 * ```
 */
export function useWindowResize(callback: () => void): void {
  const callbackRef = useRef(callback);

  // Keep ref updated with latest callback (sync before paint)
  useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    const handleResize = () => callbackRef.current();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
}
